import React, { useRef, useState } from "react";
import { apiFetch } from "@/lib/api";
import ArtifactPreview from "@/components/earnos/ArtifactPreview";

const formats = {
  zip: "application/zip",
  pdf: "application/pdf",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

export default function MissionDeliverables({ mission }) {
  const lock = useRef(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  async function download(artifact) {
    if (lock.current) return;
    lock.current = true;
    setBusy(artifact.artifact_id);
    setError("");
    try {
      const kind = artifact.kind === "offer_website" ? "zip" : artifact.kind === "presentation" ? "pptx" : artifact.kind || "pdf";
      if (!formats[kind]) throw new Error("This file format is not available for download.");
      const path = artifact.kind === "offer_website" ? "/mission/websites/" + encodeURIComponent(artifact.artifact_id) + "/download" : "/mission/artifacts/" + encodeURIComponent(artifact.artifact_id);
      const blob = await apiFetch(path, { responseType: "blob" });
      if (!blob.size || blob.type.split(";")[0] !== formats[kind]) throw new Error("The server did not return the expected file. Please retry.");
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement("a");
        link.href = url;
        link.download = (artifact.title || "EARNOS-deliverable").replace(/[^a-zA-Z0-9 _-]/g, "_").slice(0, 80) + "." + kind;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally { setTimeout(() => URL.revokeObjectURL(url), 1000); }
    } catch (e) { setError(e.message || "Download failed. Please retry."); }
    finally { lock.current = false; setBusy(null); }
  }
  return <section aria-label="Mission deliverables" className="space-y-3">
    <h3 className="font-semibold text-ink">Results and deliverables</h3>
    {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
    {!(mission.artifacts || []).length ? <p className="text-sm text-ink-2">No files have been created for this mission yet.</p> :
      mission.artifacts.map(artifact => {
        if (artifact.kind === "offer_website") return <div key={artifact.artifact_id} className="rounded-xl border border-line p-3"><p className="font-semibold text-ink">{artifact.title || "Offer website"}</p>{artifact.public_path ? <a className="text-brand-light underline" href={artifact.public_path} target="_blank" rel="noopener noreferrer">Open published website</a> : <p className="text-sm text-ink-2">Private website prepared. Publishing requires your separate approval.</p>}<ArtifactPreview key={artifact.artifact_id} artifact={artifact} mission={mission} compact /><button type="button" disabled={busy !== null} onClick={() => download(artifact)} className="mt-2 rounded-xl bg-brand px-4 py-2 text-sm font-bold text-white disabled:opacity-50">{busy === artifact.artifact_id ? "Downloading…" : "Download website ZIP"}</button></div>;
        const verified = (mission.tasks || []).some(t => t.status === "verified" && t.result?.artifact_id === artifact.artifact_id);
        const kind = artifact.kind === "offer_website" ? "zip" : artifact.kind === "presentation" ? "pptx" : artifact.kind || "pdf";
        return <div key={artifact.artifact_id} className="rounded-xl border border-line p-3">
          <p className="font-semibold text-ink">{artifact.title || "Deliverable"}</p>
          <p className="text-xs text-ink-2">{kind.toUpperCase()} · {verified ? "Passed task verification" : "Unverified output — review before use"}</p>
          {kind === "xlsx" ? <p className="text-xs text-ink-2 mt-1">Open in Excel or LibreOffice to calculate displayed SUM totals.</p> : null}
          <ArtifactPreview artifact={artifact} mission={mission} compact />
          <button type="button" disabled={busy !== null || !formats[kind]} aria-busy={busy === artifact.artifact_id}
            onClick={() => download(artifact)} className="mt-2 rounded-xl bg-brand px-4 py-2 text-sm font-bold text-white disabled:opacity-50">
            {busy === artifact.artifact_id ? "Downloading…" : "Download " + kind.toUpperCase()}
          </button>
        </div>;
      })}
  </section>;
}
